import { useTranslation } from 'react-i18next';
import { Home, Ticket, Brain, Sparkles, DollarSign, CircleDollarSign, Gauge, KeyRound, TrendingUp, Megaphone, Network } from 'lucide-react';
import { useAuth } from '@/contexts/SharedAuthContext';
import { trpc } from '@/lib/trpc';
import { ALERTS_PATH, FINOPS_ALERTS_SUBTAB } from '@/components/finops/alerts-format';
import type { UserRole } from '../../../shared/types';

/**
 * Fuente única de "qué secciones ve este usuario" — la consumen el Sidebar
 * (navegación) y el Home (mapa de secciones). Lift 1:1 de la lógica que vivía
 * inline en Sidebar.tsx; los títulos salen traducidos (t() adentro).
 *
 * El acceso por governance (config-driven) sale de UNA query, profile.getVisibility
 * (02/10/2026; antes eran 8 checkAccess por carga), con staleTime 5min + retry:false:
 * el QueryClient global no tiene staleTime, así que centralizar las opciones garantiza
 * observers idénticos entre Sidebar y Home (react-query dedupea el fetch y no dispara
 * refetches al montar ambos). El Vault sigue con su query (otra Lambda).
 */

export interface VisibleSubItem {
  title: string;
  href: string;
  /** Programa anunciado pero todavía sin sección: se muestra apagado, sin link. */
  soon?: boolean;
  /**
   * Subtítulo dentro de la sección (OML, 08/10/2026: GENERAL / PORTFOLIO / OPERACIÓN /
   * GESTIÓN). El Sidebar lo dibuja cuando cambia respecto del ítem anterior.
   */
  heading?: string;
}

export interface VisiblePeopleGroup {
  key: 'PeopleHub:learning' | 'PeopleHub:studio' | 'PeopleHub:ta' | 'PeopleHub:performance' | 'PeopleHub:users' | 'PeopleHub:devices';
  title: string;
  items: VisibleSubItem[];
}

export interface VisibleNavSection {
  /** Key estable para estado de expansión (no cambia con el idioma). */
  key: 'Tickets' | 'IANative' | 'FinancePricing' | 'FinanceBudgetControl' | 'FinOps' | 'InternalPanel' | 'Vault' | 'OwnedMedia' | 'Org';
  title: string;
  icon: React.ElementType;
  group: 'workspace' | 'technology' | 'people' | 'programs' | 'finance' | 'oml' | 'org';
  items: VisibleSubItem[];
}

export interface UseVisibleSectionsResult {
  singleNavItems: { title: string; href: string; icon: React.ElementType }[];
  /** Links de primer nivel del pill People (Landing, Community Map). */
  peopleTopLinks: VisibleSubItem[];
  peopleGroups: VisiblePeopleGroup[];
  navSections: VisibleNavSection[];
  partnerships: { canView: boolean; canAdminGovernance: boolean };
  flags: {
    isAdmin: boolean;
    canSeeUsers: boolean;
    canSeePerformance: boolean;
    canSeeTalentMap: boolean;
    canSeeIaNative: boolean;
    canSeeFinops: boolean;
    canSeeInventory: boolean;
    officeManagerOnly: boolean;
    /** Owned Media (N3): la pieza `oml` del probe único; el Home la lee de acá, no con otra query. */
    canSeeOwnedMedia: boolean;
  };
  /** Alguna de las queries de acceso sigue cargando (para skeletons del Home). */
  isAccessLoading: boolean;
  userRoles: UserRole[];
}

function hasAnyRole(userRoles: UserRole[], required: UserRole[]): boolean {
  return required.some((r) => userRoles.includes(r));
}

const ACCESS_QUERY_OPTS = { retry: false, staleTime: 5 * 60 * 1000 } as const;

export function useVisibleSections(): UseVisibleSectionsResult {
  const { t } = useTranslation();
  const { user } = useAuth();

  const userRoles: UserRole[] = user?.roles ?? (user?.role ? [user.role] : ['user']);
  const extraPerms: string[] = user?.extraPermissions ?? [];
  const deniedPerms: string[] = user?.deniedPermissions ?? [];
  const isAdmin = userRoles.includes('admin');

  const isDenied = (perm: string) => deniedPerms.includes(perm);
  const hasExtraPerm = (perm: string) => extraPerms.includes(perm);

  const canSeeUsers =
    !isDenied('tab:users') &&
    (hasAnyRole(userRoles, ['tech', 'finance', 'people', 'admin']) || hasExtraPerm('tab:users'));
  const canSeePerformance =
    !isDenied('tab:performance') &&
    (hasAnyRole(userRoles, ['people', 'admin']) || hasExtraPerm('tab:performance'));
  // Technology → FinOps: tech + finance + admin (NO growth, a diferencia de
  // subscriptionsProcedure), o permiso extra tab:finops. Misma audiencia que la
  // ruta /technology/finops en App.tsx y que finopsProcedure en el backend.
  const canSeeFinops =
    !isDenied('tab:finops') &&
    (hasAnyRole(userRoles, ['tech', 'finance', 'admin']) || hasExtraPerm('tab:finops'));

  const singleNavItems = [
    { title: t('navigation.dashboard'), href: '/dashboard', icon: Home },
    { title: t('skills.nav'), href: '/skills-agentes', icon: Sparkles },
    { title: t('hooks.visiblesections.winclap_brain'), href: '/brain', icon: Brain },
  ];

  // Cada link de Devices según el permiso que la ruta realmente acepta:
  // Inventario/Info útil ← tab:inventory; Aprobación ← tab:inventory o tab:approvals.
  const hasDeviceRole = hasAnyRole(userRoles, ['tech', 'finance', 'people', 'admin']);
  const canSeeInventoryPages =
    !isDenied('tab:inventory') && (hasDeviceRole || hasExtraPerm('tab:inventory'));
  const canSeeApprovalPage =
    !isDenied('tab:inventory') &&
    (hasDeviceRole || hasExtraPerm('tab:inventory') || hasExtraPerm('tab:approvals'));
  const deviceItems: VisibleSubItem[] = [];
  if (canSeeInventoryPages && !isDenied('subtab:inventory:main'))
    deviceItems.push({ title: t('devices.inventory'), href: '/admin/inventory' });
  if (canSeeApprovalPage && !isDenied('subtab:inventory:approval'))
    deviceItems.push({ title: t('devices.approval'), href: '/admin/devices/approval' });
  if (canSeeInventoryPages && !isDenied('subtab:inventory:useful-info'))
    deviceItems.push({ title: t('devices.useful_info'), href: '/admin/devices/useful-info' });

  // Accesos resueltos por governance config-driven (backend) — permiten que una
  // OM / manager de kits / líder del community map vea su entrada sin rol.
  // 02/10/2026: UNA query (profile.getVisibility) con los 8 resolvers en paralelo en vez de
  // una por sección (eran ~28 % del tráfico tRPC). Cada pieza viene null si no se pudo
  // verificar (el backend la loguea DEGRADED) y se lee igual que antes: "no ve".
  // Apartado nuevo con governance = una pieza más en lib/me-visibility.ts, no otra query acá.
  const { data: visibility, isLoading: visibilityLoading } = trpc.profile.getVisibility.useQuery(undefined, {
    ...ACCESS_QUERY_OPTS,
    enabled: !!user,
  });
  const omAccess = visibility?.officeManager;
  const kitsAccess = visibility?.kits;
  // D-Talk: fuera de la ventana del ciclo solo lo ve People (pedido Sol 08/09).
  const dtalkAccess = visibility?.dtalk;
  const dtalkVisible = (dtalkAccess?.canViewAll ?? false) || (dtalkAccess?.windowOpen ?? false);
  const partnershipsAccess = visibility?.partnerships;
  const taAccess = visibility?.taMetrics;
  // Owned Media (N3): aparece en el menú solo para quien tiene permiso de verlo (§6.3).
  const omlAccess = visibility?.oml;
  const orgAccess = visibility?.org;
  const financePnlAccess = visibility?.financePnl;
  const talentMapAccess = visibility?.talentMap;
  // Vault: visible solo para quien tiene ≥1 credencial compartida (o admin).
  // Otra Lambda (splitLink), por eso sigue siendo su propia query; getMyAccess nunca throwea.
  // `light`: solo canView, sin consultar la Directory API (eso lo hace VaultProtectedRoute).
  const { data: vaultAccess } = trpc.vault.getMyAccess.useQuery({ light: true }, { ...ACCESS_QUERY_OPTS, enabled: !!user });

  // Reorganización 31/08 (feedback People): el grupo "Existencial" queda con
  // Clappi + Oficinas; Pedidos e In/out (altas+bajas+inactivos IT) se mudan a
  // Devices; Community Map sube al primer nivel del pill People.
  const userItems: VisibleSubItem[] = [];
  if (canSeeUsers) {
    if (!isDenied('subtab:users:people-panel'))
      userItems.push({ title: t('users.people_panel_nav', 'Clappi'), href: '/admin/users/people-panel' });
    if (!isDenied('subtab:users:oficinas'))
      userItems.push({ title: t('users.offices', 'Oficinas'), href: '/admin/users/oficinas' });
  }
  if (canSeeUsers) {
    if (!isDenied('subtab:users:requests'))
      deviceItems.push({ title: t('users.requests'), href: '/admin/users/pedidos' });
    if (
      !isDenied('subtab:users:onboarding') ||
      !isDenied('subtab:users:offboarding') ||
      !isDenied('subtab:users:inactive')
    )
      deviceItems.push({ title: t('users.in_out', 'In/out'), href: '/admin/devices/in-out' });
  }
  // Con acceso a la sección Users, el board vive como tab dentro de "Oficinas";
  // el link directo es solo para OMs/managers de kits configuradas sin roles.
  const officeManagerOnly = Boolean((omAccess?.canView || kitsAccess?.canView) && !canSeeUsers);
  if (officeManagerOnly) {
    userItems.push({ title: t('hooks.visiblesections.office_manager'), href: '/admin/users/office-manager' });
  }

  const iaNativeItems: VisibleSubItem[] = [];
  const canSeeIaNative =
    !isDenied('tab:ia-native') &&
    (hasAnyRole(userRoles, ['growth', 'finance', 'admin']) || hasExtraPerm('tab:ia-native'));
  if (canSeeIaNative) {
    if (!isDenied('subtab:ia-native:subscriptions'))
      iaNativeItems.push({ title: t('hooks.visiblesections.ai_subscriptions'), href: '/ia-native/subscriptions' });
    if (!isDenied('subtab:ia-native:observability'))
      iaNativeItems.push({ title: t('hooks.visiblesections.ai_observability'), href: '/ia-native/observability' });
  }

  const canSeeTalentMap = talentMapAccess?.canView ?? false;
  const canSeePerformanceAdmin = isAdmin || hasExtraPerm('subtab:performance:admin');

  const performanceItems: VisibleSubItem[] = [];
  if (canSeePerformance) {
    if (!isDenied('subtab:performance:evaluations')) {
      // Pestaña "Evaluaciones" (hub EPIC/Low Performance) OCULTA para todos
      // (pedido Sol 02/09 — se van a usar más adelante). Para reactivar,
      // descomentar esta línea y revertir el redirect de App.tsx:
      // performanceItems.push({ title: t('performance.nav.evaluations'), href: '/performance' });
      //
      // El Mapa de Talento se muestra SOLO a quien puede verlo (líder con
      // gente a cargo, o roster de People). Tener rol people/admin no
      // alcanza: hasta el 14/09 acá se mostraba igual y el link llevaba a
      // una pantalla de acceso denegado — justo el lunes que se anuncia
      // D-Talk a todos, Sol no quiere que un clapper sin equipo lo vea.
      if (canSeeTalentMap)
        performanceItems.push({ title: t('performance.nav.talent_map'), href: '/performance/talent-map' });
      if (dtalkVisible) performanceItems.push({ title: t('performance.nav.dtalk'), href: '/performance/dtalk' });
    }
    if (canSeePerformanceAdmin)
      performanceItems.push({ title: t('performance.nav.admin'), href: '/performance/admin' });
  } else {
    // Sin rol People/admin: el Mapa de Talento aparece para líderes resueltos del
    // community map, y D-Talk para TODOS (cada clapper tiene su conversación —
    // la ruta es abierta y el contenido lo gatea elegibilidad/ventana del ciclo).
    if (canSeeTalentMap)
      performanceItems.push({ title: t('performance.nav.talent_map'), href: '/performance/talent-map' });
    if (dtalkVisible) performanceItems.push({ title: t('performance.nav.dtalk'), href: '/performance/dtalk' });
    if (canSeePerformanceAdmin)
      performanceItems.push({ title: t('performance.nav.admin'), href: '/performance/admin' });
  }

  // Learning (doc de Andrea, ajustes 14/09 §3): el menú es el programa vigente
  // + los dos anunciados. Los "Próximamente" no linkean a nada.
  const learningItems: VisibleSubItem[] = [
    { title: t('people_learning.nav_arcade', 'Onboarding: The Clapper Arcade'), href: '/people/learning' },
    // Sin href: todavía no hay pantalla (no es un link roto, es un anuncio)
    { title: t('people_learning.nav_lga', 'Leadership Growth Academy'), href: '', soon: true },
    { title: t('people_learning.nav_lge', 'Language Growth Experience'), href: '', soon: true },
  ];

  // Talent Acquisition (Sol 28/09): un agente = una entrada, como Learning.
  // TA Metrics (dashboard + Hiring Plan) es del roster TA; el Intake también lo
  // abre un Hiring Manager (líder con reportes directos, canIntake).
  const taItems: VisibleSubItem[] = [
    ...(taAccess?.canView ? [{ title: t('talent_acquisition.nav_metrics', 'TA Metrics'), href: '/people/talent-acquisition' }] : []),
    ...(taAccess?.canView || taAccess?.canIntake
      ? [{ title: t('talent_acquisition.nav_intake', 'Hiring Manager Intake'), href: '/people/talent-acquisition/intake' }]
      : []),
    // Interview Plan: la guía de entrevista reparte la evaluación entre TA, el
    // Hiring Manager y el Bar Raiser. TA ve todas; el HM y el Bar Raiser de una
    // búsqueda ven la suya (canInterview, TA 07/10).
    ...(taAccess?.canView || taAccess?.canInterview ? [{ title: t('talent_acquisition.nav_interview', 'Guías de entrevista'), href: '/people/talent-acquisition/interview' }] : []),
  ];

  // Winclap Studio (spec de Learning 19/10/2026 §16 + Sol 19/10): pestaña propia DENTRO del desplegable
  // de People, al lado de Learning (el Product Tour "sigue la lógica del Clapper Arcade"). Solo para
  // quien tiene funciones del Studio en el community map (o admin / viewer del roster): misma pieza
  // `studio` que gatea /studio. Sin acceso, el grupo queda vacío y el filter de abajo lo saca.
  const studioItems: VisibleSubItem[] = visibility?.studio?.canView
    ? [
        { title: t('studio.nav.programs', 'Programas'), href: '/studio' },
        { title: t('studio.nav.tour', 'Onboarding: Product Tour'), href: '/studio/product-tour' },
      ]
    : [];

  const peopleGroups: VisiblePeopleGroup[] = (
    [
      { key: 'PeopleHub:learning', title: t('people_learning.nav', 'Learning'), items: learningItems },
      { key: 'PeopleHub:studio', title: t('studio.nav.title', 'Studio'), items: studioItems },
      { key: 'PeopleHub:ta', title: t('talent_acquisition.nav', 'Talent Acquisition'), items: taItems },
      { key: 'PeopleHub:performance', title: t('navigation.performance'), items: performanceItems },
      { key: 'PeopleHub:users', title: t('navigation.users'), items: userItems },
      { key: 'PeopleHub:devices', title: t('navigation.devices'), items: deviceItems },
    ] as VisiblePeopleGroup[]
  ).filter((g) => g.items.length > 0);

  const navSections: VisibleNavSection[] = [
    // Org Service (26/10/2026): organigrama y permisos. El directorio es de TODO clapper (misma
    // audiencia que /org); "Mi equipo" solo para líderes y roster; "Administración" para admins.
    {
      key: 'Org' as const,
      title: t('org.nav.title', 'Organización'),
      icon: Network,
      group: 'org' as const,
      items: [
        { title: t('org.nav.me', 'Mi ficha'), href: '/org' },
        { title: t('org.nav.solutions', 'Soluciones'), href: '/org?tab=solutions' },
        { title: t('org.nav.people', 'Personas'), href: '/org?tab=people' },
        { title: t('org.nav.clients', 'Clientes'), href: '/org?tab=clients' },
        ...(orgAccess?.isLeader || orgAccess?.canViewAll ? [{ title: t('org.nav.team', 'Mi equipo'), href: '/org?tab=team' }] : []),
        ...(orgAccess?.canAdmin ? [{ title: t('org.nav.admin', 'Administración'), href: '/org/admin' }] : []),
        // Documentación de la API y el MCP (28/10/2026): la ve cualquiera que vea /org.
        { title: t('org.nav.api', 'API'), href: '/org/api' },
      ],
    },
    // Owned Media: repositorio de cuentas OM (N3). Governance config-driven; misma
    // audiencia que la ruta /owned-media (OmlProtectedRoute + checkAccess) y que oml/*.ts.
    // Es un área propia de Winclap (Sol 24/09): grupo OML del menú, no "Programs".
    ...(omlAccess?.canView
      ? [
          {
            key: 'OwnedMedia' as const,
            title: t('oml.nav.title', 'Owned Media'),
            icon: Megaphone,
            group: 'oml' as const,
            // Agrupado por lógica de uso (pedido del equipo de OML, 08/10/2026).
            items: [
              { heading: t('oml.nav.h_general', 'General'), title: t('oml.nav.home', 'Inicio'), href: '/owned-media/home' },
              { heading: t('oml.nav.h_portfolio', 'Portfolio'), title: t('oml.nav.accounts', 'Cuentas'), href: '/owned-media' },
              ...(omlAccess.canViewAll ? [{ heading: t('oml.nav.h_portfolio', 'Portfolio'), title: t('oml.nav.health', 'Salud del portfolio'), href: '/owned-media/health' }] : []),
              { heading: t('oml.nav.h_operation', 'Operación'), title: t('oml.nav.journeys', 'Journeys'), href: '/owned-media/journeys' },
              { heading: t('oml.nav.h_operation', 'Operación'), title: t('oml.nav.experiments', 'Experimentos'), href: '/owned-media/experiments' },
              { heading: t('oml.nav.h_operation', 'Operación'), title: t('oml.nav.projects', 'Proyectos'), href: '/owned-media/projects' },
              { heading: t('oml.nav.h_operation', 'Operación'), title: t('oml.nav.platforms', 'Plataformas y accesos'), href: '/owned-media/platforms' },
              ...(omlAccess.canAdmin || omlAccess.isDataOwner || omlAccess.isDirectorOm ? [{ heading: t('oml.nav.h_management', 'Gestión'), title: t('oml.nav.admin', 'Administración'), href: '/owned-media/admin' }] : []),
            ],
          },
        ]
      : []),
    {
      key: 'Tickets',
      title: t('navigation.tickets'),
      icon: Ticket,
      group: 'workspace',
      items: [
        { title: t('tickets.new_ticket'), href: '/tickets/select' },
        { title: t('tickets.my_tickets'), href: '/tickets/mine' },
        ...(hasAnyRole(userRoles, ['admin', 'tech', 'finance', 'people'])
          ? [{ title: t('tickets.all_tickets') ?? 'Todos los tickets', href: '/admin/tickets' }]
          : []),
      ],
    },
    ...(iaNativeItems.length > 0
      ? [{ key: 'IANative' as const, title: t('hooks.visiblesections.ia_native'), icon: Brain, group: 'programs' as const, items: iaNativeItems }]
      : []),
    // Pricing Manager (migrado de Replit): todos crean y ven SUS pricings —
    // visible para cualquier autenticado; aprobación/edición ajena se gatea server-side.
    {
      key: 'FinancePricing',
      title: t('hooks.visiblesections.pricing_manager'),
      icon: DollarSign,
      group: 'finance',
      items: [
        { title: t('hooks.visiblesections.saved_versions'), href: '/finance/pricing' },
        { title: t('hooks.visiblesections.new_pricing'), href: '/finance/pricing/new' },
        { title: 'Tutorial', href: '/finance/pricing/tutorial' },
      ],
    },
    // Control Presupuestario: P&L real vs BGT desde BigQuery — acceso por governance
    // (Juan Cruz + admins + viewers), misma audiencia que la ruta y que finance-pnl.ts.
    ...(financePnlAccess?.canView
      ? [
          {
            key: 'FinanceBudgetControl' as const,
            title: t('hooks.visiblesections.budget_control'),
            icon: TrendingUp,
            group: 'finance' as const,
            items: [
              { title: t('hooks.visiblesections.budget_control_pnl'), href: '/finance/budget-control?tab=pnl' },
              { title: t('hooks.visiblesections.budget_control_creators'), href: '/finance/budget-control?tab=creators' },
              { title: t('hooks.visiblesections.budget_control_company'), href: '/finance/budget-control?tab=company' },
            ],
          },
        ]
      : []),
    ...(canSeeFinops
      ? [
          {
            key: 'FinOps' as const,
            title: 'FinOps',
            icon: CircleDollarSign,
            group: 'technology' as const,
            items: [
              { title: 'Dashboards', href: '/technology/finops' },
              ...(!isDenied(FINOPS_ALERTS_SUBTAB)
                ? [{ title: t('finops.alerts.nav'), href: ALERTS_PATH }]
                : []),
            ],
          },
        ]
      : []),
    // Vault de Winclap: credenciales compartidas, cifradas y auditadas. Desde el 28/09/2026
    // lo ve todo clapper (canView solo es false si no se pudo resolver el acceso).
    ...(vaultAccess?.canView
      ? [
          {
            key: 'Vault' as const,
            title: t('vault.nav.title', 'Vault'),
            icon: KeyRound,
            group: 'technology' as const,
            items: [
              { title: t('vault.nav.my_secrets', 'Mis credenciales'), href: '/vault' },
              { title: t('vault.nav.groups', 'Grupos y accesos'), href: '/vault/groups' },
              { title: t('vault.nav.audit', 'Auditoría'), href: '/vault/audit' },
              { title: t('vault.nav.workshop', 'Workshop'), href: '/vault/workshop' },
              // Censo (F6): misma audiencia que la ruta (/vault/shared-accounts es admin).
              ...(vaultAccess.isAdmin ? [{ title: t('vault.nav.shared_accounts', 'Cuentas compartidas'), href: '/vault/shared-accounts' }] : []),
            ],
          },
        ]
      : []),
    // Panel de Internal: adopción/tráfico/accesos/salud de la plataforma — admin, o el
    // permiso extra subtab:internal:adoption (solo Adopción y Brain). Misma audiencia que
    // la ruta /admin/internal en App.tsx y que internalAdoptionViewerProcedure en el backend.
    ...(isAdmin || (hasExtraPerm('subtab:internal:adoption') && !isDenied('subtab:internal:adoption'))
      ? [
          {
            key: 'InternalPanel' as const,
            title: t('hooks.visiblesections.panel_de_internal'),
            icon: Gauge,
            group: 'technology' as const,
            items: [{ title: t('hooks.visiblesections.adopcion_salud_y_tareas'), href: '/admin/internal' }],
          },
        ]
      : []),
  ];

  return {
    singleNavItems,
    peopleTopLinks: [
      { title: t('people_landing.nav', 'Landing'), href: '/people/landing' },
      // Talent Acquisition es un grupo con una entrada por agente (peopleGroups)
      ...(canSeeUsers && !isDenied('subtab:users:community-map')
        ? [{ title: t('users.community_map'), href: '/admin/users/community-map' }]
        : []),
    ],
    peopleGroups,
    navSections,
    partnerships: {
      canView: partnershipsAccess?.canView ?? false,
      canAdminGovernance: partnershipsAccess?.editScopes?.includes('all') ?? false,
    },
    flags: {
      isAdmin,
      canSeeUsers,
      canSeePerformance,
      canSeeTalentMap,
      canSeeIaNative,
      canSeeFinops,
      canSeeInventory: deviceItems.length > 0,
      officeManagerOnly,
      canSeeOwnedMedia: omlAccess?.canView ?? false,
    },
    isAccessLoading: visibilityLoading,
    userRoles,
  };
}
